import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ServiceOrderWithTasks, ServiceOrderStatusColors } from '../../types/serviceOrder';
import { GanttTimeline } from './GanttTimeline';
import { GanttRow } from './GanttRow';

interface GanttData {
  projects: {
    id: string;
    name: string;
    serviceOrders: ServiceOrderWithTasks[];
  }[];
  dateRange: { start: Date; end: Date };
}

interface GanttChartProps {
  data: GanttData;
}

export const GanttChart: React.FC<GanttChartProps> = ({ data }) => {
  const navigate = useNavigate();

  // Calculate the total number of days in the date range
  const totalDays = Math.ceil(
    (data.dateRange.end.getTime() - data.dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Calculate position for a given date
  const getPositionForDate = (date: Date) => {
    const daysDiff = Math.max(0, Math.ceil(
      (date.getTime() - data.dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
    ));
    return (daysDiff / totalDays) * 100;
  };

  // Calculate width for a service order
  const getServiceOrderWidth = (serviceOrder: ServiceOrderWithTasks) => {
    const startDate = new Date(serviceOrder.startDate);
    const endDate = new Date(serviceOrder.endDate);
    const durationDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(2, (durationDays / totalDays) * 100); // Minimum 2% width
  };

  // Handle service order click
  const handleServiceOrderClick = (projectId: string, serviceOrderId: string) => {
    navigate(`/projects/${projectId}/service-orders/${serviceOrderId}`);
  };

  // Get today's position
  const todayPosition = getPositionForDate(new Date());
  const isToday = todayPosition >= 0 && todayPosition <= 100;

  return (
    <div className="w-full">
      {/* Timeline Header */}
      <div className="relative mb-4">
        <GanttTimeline 
          startDate={data.dateRange.start} 
          endDate={data.dateRange.end} 
        />
        
        {/* Today Indicator */}
        {isToday && (
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-blue-400 z-10"
            style={{ left: `${todayPosition}%` }}
          >
            <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-400 rounded-full"></div>
          </div>
        )}
      </div>

      {/* Project Rows */}
      <div className="space-y-4">
        {data.projects.map((project) => (
          <GanttRow
            key={project.id}
            project={project}
            dateRange={data.dateRange}
            getPositionForDate={getPositionForDate}
            getServiceOrderWidth={getServiceOrderWidth}
            onServiceOrderClick={handleServiceOrderClick}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 p-4 bg-white/5 rounded-lg">
        <h4 className="text-sm font-medium text-foreground-primary mb-3">Status das Ordens de Serviço</h4>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500/20 border border-blue-500/30"></div>
            <span className="text-sm text-foreground-secondary">Agendado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500/20 border border-green-500/30"></div>
            <span className="text-sm text-foreground-secondary">Em Andamento</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-500/20 border border-gray-500/30"></div>
            <span className="text-sm text-foreground-secondary">Encerrado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500/20 border border-red-500/30"></div>
            <span className="text-sm text-foreground-secondary">Inativo</span>
          </div>
        </div>
      </div>
    </div>
  );
};